/**
 * Step 4 "Log" (Sofia, Oct 24 2026; was "Replies"): everyone on the role by stage — Shortlisted ·
 * InMail sent · Follow-up sent · Replied · Booked (`logStages.ts`; Booked since Nov 2 2026). A sent person's row opens their card
 * underneath, with the follow-up, Lever and "They replied" actions the Replies step had.
 */
import { useState } from 'react';
import { Input } from '@finn-auto/design-system/atoms/input';
import type { OutreachCandidate, OutreachConfig } from '../../types/taOutreach';
import { OutreachCard } from './OutreachCard';
import { buildLog, fmtBookedAt, leverHandoverText, LOG_STAGE_LABEL, LOG_STAGES, type LogEntry, type LogStage } from './logStages';
import { profileLinkOf } from './profileLink';
import { OwnerName } from './outreachUi';

const STAGE_HINT: Record<LogStage, string> = {
  shortlisted: 'On the shortlist or accepted, no InMail out yet.',
  sent: 'Waiting for a reply; the follow-up is not due yet.',
  followed_up: 'No reply, and the follow-up date has passed. LinkedIn sends its own follow-up by itself, so it is counted as sent.',
  replied: 'They answered. Lever follows once they book a call.',
  booked: 'They booked a call through the InMail. That hands them over to Lever, and the outreach for them ends there.',
};

export function OutreachLog({
  items,
  config,
  followUpDays,
  expanded,
  onToggle,
  onUpdated,
}: {
  items: OutreachCandidate[];
  config: OutreachConfig;
  followUpDays: number;
  expanded: string | null;
  onToggle: (id: string) => void;
  onUpdated: (item: OutreachCandidate) => void;
}) {
  const [now] = useState(() => Date.now());
  const [q, setQ] = useState('');
  const all = buildLog(items, now, followUpDays);
  if (!LOG_STAGES.some((s) => all[s].length)) return <p className="text-[13px] text-[#707070]">Nobody on this role yet.</p>;
  // Search by name (Sofia, Oct 24 2026): every stage keeps only the matches, and a stage with a match
  // opens — Shortlisted too — so the person is found wherever they stand.
  const needle = q.trim().toLowerCase();
  const log = needle ? matchingLog(all, needle) : all;
  const hits = LOG_STAGES.reduce((n, s) => n + log[s].length, 0);
  return (
    <div className="space-y-3">
      <div className="max-w-sm">
        <Input label="Search a person" type="search" value={q} maxLength={100} onChange={(e) => setQ(e.target.value)} />
      </div>
      {needle && !hits && <p className="text-[13px] text-[#707070]">Nobody on this role matches “{q.trim()}”.</p>}
      {[...LOG_STAGES].reverse().map((stage) => (
        <details
          key={`${stage}:${needle ? 'q' : ''}`}
          open={needle ? log[stage].length > 0 : stage !== 'shortlisted' && log[stage].length > 0}
          className="rounded-md border border-solid border-[#E9EAEC] bg-white px-4 py-3"
        >
          <summary className="cursor-pointer text-sm font-semibold text-[#1A1A1A]">
            {LOG_STAGE_LABEL[stage]} ({log[stage].length})
          </summary>
          <p className="mb-2 mt-1 text-xs text-[#707070]">{STAGE_HINT[stage]}</p>
          {log[stage].length ? (
            <ul className="m-0 list-none space-y-2 p-0">
              {log[stage].map((e) => (
                <LogRow
                  key={e.candidate.id}
                  entry={e}
                  config={config}
                  open={expanded === e.candidate.id}
                  onToggle={() => onToggle(e.candidate.id)}
                  onUpdated={onUpdated}
                />
              ))}
            </ul>
          ) : (
            <p className="text-[13px] text-[#707070]">Nobody here.</p>
          )}
        </details>
      ))}
    </div>
  );
}

function matchingLog(log: Record<LogStage, LogEntry[]>, needle: string): Record<LogStage, LogEntry[]> {
  const out = { ...log };
  for (const s of LOG_STAGES) out[s] = log[s].filter((e) => (e.candidate.name || '').toLowerCase().includes(needle));
  return out;
}

function LogRow({
  entry,
  config,
  open,
  onToggle,
  onUpdated,
}: {
  entry: LogEntry;
  config: OutreachConfig;
  open: boolean;
  onToggle: () => void;
  onUpdated: (item: OutreachCandidate) => void;
}) {
  const c = entry.candidate;
  const link = profileLinkOf(c);
  const sent = c.status === 'sent';
  const lever = leverHandoverText(c);
  return (
    <li className="text-[13px]">
      <div className="flex flex-wrap items-center gap-2">
        {link ? (
          <a href={link} target="_blank" rel="noreferrer" className="font-semibold text-[#1A1A1A] underline">
            {c.name}
          </a>
        ) : (
          <span className="font-semibold">{c.name}</span>
        )}
        <OwnerName owner={c.owner} />
        {sent && (
          <button type="button" onClick={onToggle} aria-expanded={open} className="text-xs font-semibold text-[#0072EA] underline">
            {open ? 'Hide details' : 'Details'}
          </button>
        )}
      </div>
      {c.booking?.start && <p className="ml-3 mt-1 text-xs text-[#1A1A1A]">Call booked for {fmtBookedAt(c.booking.start)}</p>}
      {c.reply?.preview && <p className="ml-3 mt-1 text-xs text-[#1A1A1A]">“{c.reply.preview}”</p>}
      {lever && (
        <p className={`ml-3 mt-1 text-xs ${lever.tone === 'warn' ? 'text-[#B96716]' : lever.tone === 'done' ? 'text-[#1B7F3B]' : 'text-[#707070]'}`}>
          {lever.text}
          {lever.tone === 'done' && c.lever?.url && (
            <>
              {' · '}
              <a href={c.lever.url} target="_blank" rel="noreferrer" className="text-[#0072EA] underline">
                Open in Lever
              </a>
            </>
          )}
        </p>
      )}
      {sent && open && (
        <div className="mt-2">
          <OutreachCard candidate={c} config={config} expanded drafting={false} onToggle={onToggle} onDraft={() => {}} onUpdated={onUpdated} />
        </div>
      )}
    </li>
  );
}
